import type { User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { DuplicateEmailError } from '@/constants/signupErrors';
import { logger } from '@/utils/logger';

/** Minimal profile row shape from `profiles` (used after fetch / OAuth bootstrap). */
export type ProfileRow = {
  id: string;
  email: string;
  role?: string;
  subscription?: unknown;
  [key: string]: unknown;
};

export function normalizeProfileEmail(raw: string): string {
  return String(raw).trim().toLowerCase();
}

export function parseProfileRow(data: unknown): ProfileRow | null {
  if (!data || typeof data !== 'object') return null;
  const row = data as Record<string, unknown>;
  if (typeof row.id !== 'string') return null;
  const email = typeof row.email === 'string' ? row.email : '';
  return { ...row, id: row.id, email } as ProfileRow;
}

function parseNameFromAuthUser(authUser: User): { first_name?: string; last_name?: string } {
  const meta = authUser.user_metadata ?? {};
  const fullName =
    (typeof meta.full_name === 'string' && meta.full_name.trim()) ||
    (typeof meta.name === 'string' && meta.name.trim()) ||
    '';

  if (fullName) {
    const parts = fullName.split(/\s+/);
    if (parts.length === 1) return { first_name: parts[0] };
    return { first_name: parts[0], last_name: parts.slice(1).join(' ') };
  }

  if (typeof meta.given_name === 'string' && meta.given_name.trim()) {
    return {
      first_name: meta.given_name.trim(),
      last_name:
        typeof meta.family_name === 'string' && meta.family_name.trim()
          ? meta.family_name.trim()
          : undefined,
    };
  }

  return {};
}

function resolveAuthEmail(authUser: User): string {
  let raw = '';
  if (authUser.email?.trim()) raw = authUser.email.trim();
  else {
    const meta = authUser.user_metadata ?? {};
    if (typeof meta.email === 'string' && meta.email.trim()) raw = meta.email.trim();
  }
  return raw ? normalizeProfileEmail(raw) : '';
}

export async function fetchProfileByUserId(userId: string) {
  return supabase.from('profiles').select('*').eq('id', userId).maybeSingle();
}

type EnsureOAuthProfileResponse = {
  success?: boolean;
  profile?: unknown;
  error?: string;
  message?: string;
};

async function signOutLocal() {
  try {
    await supabase.auth.signOut({ scope: 'local' });
  } catch {
    // ignore
  }
}

async function ensureProfileViaEdgeFunction(): Promise<ProfileRow> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Not authenticated');
  }

  const { data, error } = await supabase.functions.invoke<EnsureOAuthProfileResponse>(
    'ensure-oauth-profile',
    {
      headers: { Authorization: `Bearer ${session.access_token}` },
    },
  );

  if (error) {
    logger.error('ensure-oauth-profile invoke error:', error);
    throw error;
  }

  if (!data?.success) {
    if (data?.error === 'EMAIL_ALREADY_EXISTS') {
      await signOutLocal();
      throw new DuplicateEmailError();
    }
    throw new Error(data?.error || data?.message || 'Failed to create profile');
  }

  const row = parseProfileRow(data.profile);
  if (!row) {
    throw new Error('Invalid profile response from server');
  }
  return row;
}

async function ensureProfileViaClientInsert(authUser: User): Promise<ProfileRow> {
  const { first_name, last_name } = parseNameFromAuthUser(authUser);
  const email = resolveAuthEmail(authUser);

  const row: Record<string, unknown> = {
    id: authUser.id,
    email,
    role: 'volunteer',
    hours: 0,
    points: 0,
    created_at: new Date().toISOString(),
    onboarding_completed: false,
  };
  if (first_name) row.first_name = first_name;
  if (last_name) row.last_name = last_name;

  const { error: insertError } = await supabase.from('profiles').insert([row]);
  if (insertError) {
    if (insertError.code === '23505') {
      const { data: retry, error: retryError } = await fetchProfileByUserId(authUser.id);
      if (retryError) throw retryError;
      const retryRow = parseProfileRow(retry);
      if (retryRow) return retryRow;
      await signOutLocal();
      throw new DuplicateEmailError();
    }
    throw insertError;
  }

  const { data: created, error: reloadError } = await fetchProfileByUserId(authUser.id);
  if (reloadError) throw reloadError;
  const createdRow = parseProfileRow(created);
  if (!createdRow) {
    throw new Error('Profile was created but could not be loaded');
  }
  return createdRow;
}

/**
 * Ensures a volunteer profile exists for OAuth / first sign-in (avoids PostgREST 406 on .single()).
 *
 * If a profile already exists for this auth user, it is returned as-is — signing in with the same
 * social identity (even via the "sign up with Google/Apple" button) logs the user in rather than
 * being rejected as a duplicate. A genuine cross-identity email collision (a profile with the same
 * email but a different id) is still caught by `ensure-oauth-profile` and surfaced as
 * `DuplicateEmailError`.
 */
export async function ensureVolunteerProfile(authUser: User): Promise<ProfileRow> {
  const { data: existing, error: fetchError } = await fetchProfileByUserId(authUser.id);
  if (fetchError) throw fetchError;
  const existingRow = parseProfileRow(existing);
  if (existingRow) return existingRow;

  try {
    return await ensureProfileViaEdgeFunction();
  } catch (edgeError) {
    if (edgeError instanceof DuplicateEmailError) {
      throw edgeError;
    }
    logger.warn('ensureVolunteerProfile: edge function failed, trying client insert', edgeError);
    return ensureProfileViaClientInsert(authUser);
  }
}
